import { useEffect, useState } from 'react';
import serverErrorHandler from 'services/serverError.service';
import {
  getFormValidationStatusAsync,
  scrollToFormErr,
} from 'utils/form.util';
import type { FormField } from '@urbantxt/form';
import { openSuccessToaster } from 'services/toast.service';
import useUserStore from 'store/useUserStore';
import bagelSmsConsent from '../api/bagelSmsConsent';
import getUserSession from '../api/getUserSession';
import { bagelSmsConsentFormModel as model } from '../validation/schemas';

export interface IBagelSmsConsentForm {
  phoneNumber: string;
  // Manually-rendered checkbox (see BagelSmsConsentForm) — ['consent'] when
  // checked, [] when not. NOT routed through CustomForm's `checkbox` field
  // type: @urbantxt/form's CustomFormField has no case for it, so it falls
  // into the generic fallback branch that drops the `checkboxes` prop
  // entirely, crashing CheckboxFormFieldImpl on `checkboxes.map(...)`.
  consent: string[];
}

export const CONSENT_LABEL =
  'I agree to receive text messages from Bagel AI (URBAN TXT) at the phone number provided, ' +
  'including website-building assistance and status updates. Message frequency varies. ' +
  'Message and data rates may apply. Reply STOP to opt out, HELP for help.';

const fields: FormField[] = [
  {
    name: 'phoneNumber',
    type: 'phoneNumber',
    label: 'Mobile Phone Number',
    size: 'lg',
    data: null,
  },
];

export default function useBagelSmsConsent() {
  const user = useUserStore((s) => s.user);

  const [formValue, setFormValue] = useState<IBagelSmsConsentForm>({
    phoneNumber: user?.bagelSmsPhoneNumber ?? '',
    // Always starts unchecked, even on a repeat visit — each save is a fresh,
    // active affirmative action rather than an assumed-carried-over consent.
    consent: [],
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  // Prefill once the session's phone number is available (e.g. first render
  // before useAuth() resolves would otherwise lock in an empty string).
  useEffect(() => {
    if (user?.bagelSmsPhoneNumber) {
      setFormValue((prev) => ({ ...prev, phoneNumber: user.bagelSmsPhoneNumber as string }));
    }
  }, [user?.bagelSmsPhoneNumber]);

  const onSubmit = async () => {
    try {
      const isValid = await getFormValidationStatusAsync(model.check(formValue));

      if (isValid) {
        setIsLoading(true);

        await bagelSmsConsent({
          phoneNumber: formValue.phoneNumber,
          consent: formValue.consent.includes('consent'),
        });

        const updatedUser = await getUserSession();
        useUserStore.getState().setUser(updatedUser);

        openSuccessToaster('SMS consent recorded.', 3500);
        setIsLoading(false);
        setIsSubmitted(true);
      } else {
        scrollToFormErr();
      }
    } catch (e) {
      setIsLoading(false);
      serverErrorHandler(e);
    }
  };

  const onChange = (val: any) => {
    setFormValue(val);
  };

  const setConsent = (checked: boolean) => {
    setFormValue((prev) => ({ ...prev, consent: checked ? ['consent'] : [] }));
  };

  return {
    fields,
    formValue,
    isLoading,
    isSubmitted,
    isLoggedIn: Boolean(user),
    model,
    onChange,
    onSubmit,
    setConsent,
  };
}
